import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { to12h } from "@/lib/datetime";
import {
  Trash2, Ban, Camera, Send, BellOff, Receipt, FileSignature, ChevronRight,
  DollarSign, ShieldCheck, Car, FileText, Gift, Loader2, AlertTriangle,
} from "lucide-react";
import FinancialsEditor from "@/components/admin/FinancialsEditor";
import { BookingPricing } from "@/components/admin/BookingPricing";
import AddressVerify from "@/components/admin/AddressVerify";
import DamageCharge from "@/components/admin/DamageCharge";
import ChargeCard from "@/components/admin/ChargeCard";
import CardOnFile from "@/components/admin/CardOnFile";
import ReservationPayment from "@/components/admin/ReservationPayment";
import PaymentNudge from "@/components/admin/PaymentNudge";
import OweButton from "@/components/admin/OweButton";
import ClubPointsButton from "@/components/admin/ClubPointsButton";
import AutoRenewToggle from "@/components/admin/AutoRenewToggle";
import DailyRollingToggle from "@/components/admin/DailyRollingToggle";
import WeeklyOngoingToggle from "@/components/admin/WeeklyOngoingToggle";
import ReturnCompliancePanel from "@/components/admin/ReturnCompliancePanel";
import RiskBadge from "@/components/admin/RiskBadge";
import ExtendRental from "@/components/admin/ExtendRental";
import RentalTimeline from "@/components/admin/RentalTimeline";
import HamptonPaymentLedgerFixCard from "@/components/admin/HamptonPaymentLedgerFixCard";
import CrmStatusChip from "@/components/admin/CrmStatusChip";
import EditBookingDates from "@/components/admin/EditBookingDates";
import SwapCar from "@/components/admin/SwapCar";
import LifecycleStatus from "@/components/admin/LifecycleStatus";
import InsuranceVerify from "@/components/admin/InsuranceVerify";
import InspectionPanel from "@/components/admin/InspectionPanel";
import ReverifyDocs from "@/components/admin/ReverifyDocs";
import AdminDocUpload, { DocFile } from "@/components/admin/AdminDocUpload";
import EmailDetective from "@/components/admin/EmailDetective";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DnrBadge = ({ item, id }) => item?.dnr ? (
  <span data-testid={`dnr-warning-${id}`} title={item.dnr_reason ? `Reason: ${item.dnr_reason}` : "On the Do-Not-Rent list"}
    className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/50 animate-pulse">
    <Ban size={10} /> DO NOT RENT{item.dnr_reason ? ` — ${item.dnr_reason}` : ""}
  </span>
) : null;

function DocImage({ path, label, token }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!path) return;
    let active = true;
    let objUrl;
    axios.get(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((res) => { if (active) { objUrl = URL.createObjectURL(res.data); setUrl(objUrl); } })
      .catch(() => active && setErr(true));
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path, token]);
  if (!path) return null;
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" className="block w-28">
      <div className="h-20 w-28 rounded-md border border-white/15 overflow-hidden bg-black flex items-center justify-center">
        {err ? <span className="text-white/30 text-xs">unavailable</span>
          : url ? <img src={url} alt={label} className="w-full h-full object-cover" />
          : <Loader2 className="animate-spin text-white/40" size={16} />}
      </div>
      <span className="text-[11px] text-white/45 mt-1 block text-center">{label}</span>
    </a>
  );
}

const Alert = ({ children, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-1 rounded bg-red-500/15 text-red-300 border border-red-500/50">
    <AlertTriangle size={10} /> {children}
  </span>
);
const WarnAlert = ({ children, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-1 rounded bg-amber-400/10 text-amber-300 border border-amber-400/50">
    <AlertTriangle size={10} /> {children}
  </span>
);

function Section({ id, bookingId, icon, title, summary, attention, open, onToggle, children }) {
  return (
    <div className={`rounded-lg border overflow-hidden ${attention ? "border-amber-500/40 bg-amber-500/[0.03]" : "border-white/10 bg-[#0A0A0A]"}`}>
      <button data-testid={`section-${id}-${bookingId}`} onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-3.5 py-3 text-left hover:bg-white/[0.03] transition-colors">
        <span className="flex items-center gap-2.5 min-w-0">
          <span className={attention ? "text-amber-400" : "text-[#F2B705]"}>{icon}</span>
          <span className="min-w-0">
            <span className="block text-sm font-bold">{title}</span>
            <span data-testid={`section-summary-${id}-${bookingId}`} className={`block text-xs truncate ${attention ? "text-amber-300 font-semibold" : "text-white/45"}`}>{summary}</span>
          </span>
        </span>
        <ChevronRight size={16} className={`shrink-0 text-white/40 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div data-testid={`section-body-${id}-${bookingId}`} className="px-3.5 pb-4 pt-2 border-t border-white/5">{children}</div>}
    </div>
  );
}

export default function BookingCard({ b, token, agreement, selected, crmMeta, resyncing, actions, fmt }) {
  const {
    toggleSelect, deleteBooking, resyncCrm, load, logout, setApproveFor, setReceiptFor,
    reissueAgreement, cancelBooking, sendSelfServiceLink, toggleEscalation, openPdf, resendLink, markSigned,
  } = actions;

  const [accidentFlag, setAccidentFlag] = useState(null);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/accident-reports?booking_id=${b.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!active) return;
        const rows = data.reports || [];
        setAccidentFlag({ count: rows.length, fatal: rows.some((r) => r.fatal) });
      }).catch(() => {});
    return () => { active = false; };
  }, [b.id, token]);
  const hasFatalAccident = !!accidentFlag?.fatal;


  const total = Number(b.total_price) || 0;
  const paid = Number(b.amount_paid) || 0;
  const balance = Math.max(0, total - (Number(b.discount) || 0) - paid);

  const today = new Date().toISOString().slice(0, 10);
  const ret = (b.return_date || "").slice(0, 10);
  const rentalDone = ["returned", "closed"].includes(b.lifecycle_status);
  const wr = b.weekly_rolling || {};
  const ongoingWeekly = !!wr.enabled && !rentalDone;
  const weeklyPaidThrough = (wr.paid_through || "").slice(0, 10);
  const weeklyNeedsAnchor = ongoingWeekly && !weeklyPaidThrough;
  const weeklyOverdue = ongoingWeekly && !!weeklyPaidThrough && weeklyPaidThrough < today;
  // Ongoing Weekly Rental has no fixed return date — a stale/placeholder return_date must
  // never generate a false OVERDUE flag; only an explicit, admin-set paid-through date can.
  const overdue = !ongoingWeekly && !!ret && ret < today && !rentalDone;
  const soon = new Date(); soon.setDate(soon.getDate() + 2);
  const dueSoon = !ongoingWeekly && !overdue && !!ret && ret <= soon.toISOString().slice(0, 10) && ret >= today && !rentalDone;

  const iv = b.insurance_verification;
  const insExpired = iv?.status === "expired";
  const insNeedsReview = !!iv && iv.status !== "valid" && !insExpired;
  const insProblem = insExpired || insNeedsReview || !!b.reverify_docs || (!!b.insurance_bring_later && !b.insurance_card_path);
  const insLabel = hasFatalAccident ? "🚨 FATAL ACCIDENT ON FILE — open to send to detective" : insExpired ? "⚠️ Insurance EXPIRED" : insProblem ? "⚠️ Insurance Needs Attention"
    : iv?.status === "valid" ? `✅ Verified${iv.expiration_date ? ` — exp. ${iv.expiration_date}` : ""}`
    : b.coverage_selection === "rental" ? "Rental coverage selected"
    : b.coverage_selection === "decline" ? "Coverage declined"
    : b.insurance_card_path ? "Card on file — not reviewed" : "Not checked";

  const docsMissing = !b.license_front_path && !b.selfie_path;
  const cof = b.card_on_file;
  const noCardOnFile = !cof?.last4 && !rentalDone;
  const cardExpired = !!cof?.last4 && !rentalDone && (() => {
    const ey = Number(cof.exp_year), em = Number(cof.exp_month);
    if (!ey || !em) return false;
    const now = new Date();
    return ey < now.getFullYear() || (ey === now.getFullYear() && em < now.getMonth() + 1);
  })();
  const alreadyHandedOver = b.lifecycle_status === "active";
  const hasDamage = (b.pricing?.lines || []).some((l) => String(l.key || "").startsWith("damage_"));
  const rollingDue = b.daily_rolling?.enabled && (b.daily_rolling.paid_through || "").slice(0, 10) <= today;
  const awaitingReservationPayment = b.reservation_payment_required && b.reservation_payment_status !== "paid";
  const reservationRefundDue = b.reservation_payment_refund_due;
  const reservationRefundReview = b.reservation_payment_refund_review_needed;

  const statusLabel = { new: "New", confirmed: "Confirmed", active: "Active", returned: "Returned", closed: "Closed" }[b.lifecycle_status || "new"] || "New";
  const agrStatus = agreement?.status === "signed" ? "✅ Agreement Signed"
    : agreement ? "Pending signature" : (b.status === "approved" || b.agreement_id) ? "Agreement created" : "No agreement yet";

  const [open, setOpen] = useState(() => {
    if (overdue || rollingDue || weeklyOverdue) return "rental";
    if (insProblem) return "insurance";
    if (balance > 0) return "payments";
    if (hasDamage) return "inspections";
    return null;
  });
  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

  return (
    <div data-testid={`booking-row-${b.id}`} className={`bg-[#0C0C0C] border rounded-lg p-4 sm:p-5 ${selected ? "border-[#F2B705]/50" : "border-white/10"}`}>
      {/* ---------- Always-visible summary ---------- */}
      <div className="flex flex-wrap justify-between gap-2">
        <div className="flex items-start gap-3 min-w-0">
          <input type="checkbox" data-testid={`booking-select-${b.id}`} checked={selected} onChange={() => toggleSelect(b.id)}
            className="h-4 w-4 mt-1.5 accent-[#F2B705] shrink-0" />
          <div className="min-w-0">
            <span className="font-display font-bold text-lg">{b.name}</span>
            <span className="ml-3 text-[#F2B705] font-semibold">{b.vehicle}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <CrmStatusChip booking={b} onResync={resyncCrm} resyncing={resyncing} locationId={crmMeta.location_id} pipelineName={crmMeta.pipeline_name} />
          <span className="text-xs text-white/40 hidden sm:inline">{fmt(b.created_at)}</span>
          <button data-testid={`booking-delete-${b.id}`} onClick={() => deleteBooking(b.id)} title="Delete booking"
            className="text-white/40 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-white/65">
        <span>📞 {b.phone}</span>
        <span className="break-all">✉️ {b.email}</span>
        <span>🗓 {b.pickup_date}{b.pickup_time ? ` ${to12h(b.pickup_time)}` : ""} → {b.return_date}{b.return_time ? ` ${to12h(b.return_time)}` : ""}{b.days ? ` · ${b.days} day${b.days > 1 ? "s" : ""}` : ""}</span>
        {b.ghl_sms_sent ? <span className="text-[#F2B705]" data-testid={`booking-sms-sent-${b.id}`}>✓ SMS sent</span> : <span className="text-white/35">SMS pending</span>}
      </div>

      {((b.coverage_selection || b.insurance_type) || b.add_extra_driver || b.add_damage_protection || b.young_driver_fee || b.date_of_birth) && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(b.coverage_selection || b.insurance_type) && <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white/55">{b.coverage_selection === "rental" ? "Opt 2: Rental Coverage" : b.coverage_selection === "decline" ? "Opt 3: Declined" : b.coverage_selection === "own" ? "Opt 1: Own Insurance" : (b.insurance_type === "AllIn" ? "All-In Coverage" : "BYOI")}</span>}
          {b.add_extra_driver && <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white/55">+ Extra driver{b.extra_driver_name ? `: ${b.extra_driver_name}` : ""}</span>}
          {b.add_damage_protection && <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white/55">+ Damage Waiver $15/day</span>}
          {b.young_driver_fee && <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white/55">Young driver fee</span>}
          {b.date_of_birth && <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white/55">DOB {b.date_of_birth}</span>}
        </div>
      )}

      {/* critical alerts — never hidden */}
      {(b.dnr || overdue || dueSoon || balance > 0 || insProblem || docsMissing || b.agreement_stale || rollingDue || hasFatalAccident || noCardOnFile || cardExpired || awaitingReservationPayment || reservationRefundDue || reservationRefundReview || weeklyOverdue || weeklyNeedsAnchor) && (
        <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid={`booking-alerts-${b.id}`}>
          <DnrBadge item={b} id={b.id} />
          {awaitingReservationPayment && (
            <button data-testid={`alert-reservation-payment-${b.id}`} onClick={() => setOpen("payments")}
              className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-1 rounded bg-amber-400/10 text-amber-300 border border-amber-400/50 hover:bg-amber-400 hover:text-black transition-colors animate-pulse">
              <AlertTriangle size={10} /> Awaiting $20 reservation payment
            </button>
          )}
          {reservationRefundDue && (
            <button data-testid={`alert-reservation-refund-due-${b.id}`} onClick={() => setOpen("payments")}
              className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-1 rounded bg-sky-400/10 text-sky-300 border border-sky-400/50 hover:bg-sky-400 hover:text-black transition-colors">
              <AlertTriangle size={10} /> $20 refund due (canceled)
            </button>
          )}
          {reservationRefundReview && (
            <button data-testid={`alert-reservation-refund-review-${b.id}`} onClick={() => setOpen("payments")}
              className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-1 rounded bg-white/5 text-white/70 border border-white/25 hover:bg-white/15 transition-colors">
              <AlertTriangle size={10} /> $20 refund review needed
            </button>
          )}
          {hasFatalAccident && (
            <button data-testid={`alert-fatal-accident-${b.id}`} onClick={() => setOpen("insurance")}
              className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-1 rounded bg-red-600/25 text-red-300 border border-red-600 animate-pulse hover:bg-red-600 hover:text-white transition-colors">
              <AlertTriangle size={10} /> Fatal Accident — Click to Send to Detective
            </button>
          )}
          {overdue && <Alert testid={`alert-overdue-${b.id}`}>Overdue — return was {ret}</Alert>}
          {weeklyOverdue && <Alert testid={`alert-weekly-overdue-${b.id}`}>Ongoing Weekly — past paid-through ({weeklyPaidThrough})</Alert>}
          {weeklyNeedsAnchor && <WarnAlert testid={`alert-weekly-anchor-${b.id}`}>Ongoing Weekly — no billing anchor date set</WarnAlert>}
          {rollingDue && <Alert testid={`alert-rolling-due-${b.id}`}>Daily rolling payment due</Alert>}
          {dueSoon && <WarnAlert testid={`alert-due-soon-${b.id}`}>Return due soon — {ret}</WarnAlert>}
          {balance > 0 && <Alert testid={`alert-balance-${b.id}`}>Balance owed {money(balance)}</Alert>}
          {insExpired && <Alert testid={`alert-ins-expired-${b.id}`}>Insurance expired</Alert>}
          {!insExpired && insProblem && <WarnAlert testid={`alert-ins-review-${b.id}`}>Insurance needs review</WarnAlert>}
          {docsMissing && <WarnAlert testid={`alert-docs-${b.id}`}>Documents missing</WarnAlert>}
          {noCardOnFile && (
            <button data-testid={`alert-no-card-${b.id}`} onClick={() => setOpen("payments")}
              className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-1 rounded bg-amber-400/10 text-amber-300 border border-amber-400/50 hover:bg-amber-400 hover:text-black transition-colors">
              <AlertTriangle size={10} /> {alreadyHandedOver ? "No card on file — add one for insurance" : "No card on file — required before handover"}
            </button>
          )}
          {cardExpired && (
            <button data-testid={`alert-card-expired-${b.id}`} onClick={() => setOpen("payments")}
              className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-1 rounded bg-red-500/15 text-red-300 border border-red-500/50 hover:bg-red-500 hover:text-white transition-colors">
              <AlertTriangle size={10} /> Card on file expired — replace it
            </button>
          )}
          {b.agreement_stale && <WarnAlert testid={`alert-agr-stale-${b.id}`}>{b.agreement_stale_reason || "Agreement out of date"}</WarnAlert>}
        </div>
      )}

      {/* compact stats + status + primary action */}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <LifecycleStatus booking={b} token={token} onAuthError={logout} onSaved={load} />
        <span data-testid={`stat-total-${b.id}`} className="text-xs px-2.5 py-1 rounded-full border border-white/15 text-white/70">Total <b className="text-white">{money(total)}</b></span>
        <span data-testid={`stat-paid-${b.id}`} className="text-xs px-2.5 py-1 rounded-full border border-emerald-500/30 text-emerald-300">Paid {money(paid)}</span>
        <span data-testid={`stat-balance-${b.id}`} className={`text-xs px-2.5 py-1 rounded-full border ${balance > 0 ? "border-red-500/50 text-red-300 font-bold" : "border-white/15 text-white/50"}`}>Balance {money(balance)}</span>
        {ongoingWeekly && (
          <span data-testid={`weekly-rolling-badge-${b.id}`} className={`text-[10px] font-black px-2 py-0.5 rounded border ${weeklyOverdue ? "border-red-500/60 text-red-300 bg-red-500/10" : weeklyNeedsAnchor ? "border-amber-400/60 text-amber-300 bg-amber-400/10" : "border-sky-500/50 text-sky-300 bg-sky-500/10"}`}>
            🔁 Ongoing Weekly{wr.agreed_rate ? ` · $${Number(wr.agreed_rate).toFixed(0)}/wk` : ""} · {weeklyOverdue ? `PAST PAID-THROUGH ${weeklyPaidThrough}` : weeklyNeedsAnchor ? "NO BILLING ANCHOR" : `PAID THRU ${weeklyPaidThrough}`}
          </span>
        )}
        {b.daily_rolling?.enabled && (
          <span data-testid={`rolling-badge-${b.id}`} className={`text-[10px] font-black px-2 py-0.5 rounded border ${rollingDue ? "border-red-500/60 text-red-300 bg-red-500/10" : "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"}`}>
            🔄 ${Number(b.daily_rolling.daily_rate).toFixed(0)}/day · {rollingDue ? `DUE — paid thru ${(b.daily_rolling.paid_through || "").slice(0, 10)}` : `PAID THRU ${(b.daily_rolling.paid_through || "").slice(0, 10)}`}
          </span>
        )}
        {!(b.status === "approved" || b.agreement_id) && (
          <button data-testid={`approve-booking-${b.id}`} onClick={() => setApproveFor(b)}
            className="bg-[#F2B705] text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-white transition-colors">
            Approve &amp; Generate Agreement
          </button>
        )}
      </div>

      {/* ---------- Collapsible sections ---------- */}
      <div className="mt-4 space-y-2">
        <Section id="payments" bookingId={b.id} icon={<DollarSign size={17} />} title="Payments & Financials"
          summary={`Paid ${money(paid)} | Balance ${money(balance)}`} attention={balance > 0}
          open={open === "payments"} onToggle={() => toggle("payments")}>
          <FinancialsEditor booking={b} token={token} onAuthError={logout} onSaved={load} />
          <HamptonPaymentLedgerFixCard bookingId={b.id} token={token} onApplied={load} />
          <BookingPricing booking={b} token={token} onSaved={load} />
          {b.reservation_payment_required && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="text-[11px] uppercase tracking-widest text-white/35 mb-1.5">$20 Reservation Payment (credited toward rental total)</div>
              <ReservationPayment booking={b} token={token} onSaved={load} />
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="text-[11px] uppercase tracking-widest text-white/35 mb-1.5">Card on File (insurance requirement — no deposit)</div>
            <CardOnFile booking={b} token={token} onSaved={load} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button data-testid={`receipt-btn-${b.id}`} onClick={() => setReceiptFor(b)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#F2B705]/35 text-[#F2B705] text-xs font-bold hover:bg-[#F2B705] hover:text-black transition-colors">
              <Receipt size={13} /> Receipt
            </button>
            <OweButton booking={b} token={token} onSaved={load} />
            <PaymentNudge booking={b} token={token} onSaved={load} />
            <ChargeCard booking={b} token={token} onSaved={load} />
          </div>
        </Section>

        <Section id="insurance" bookingId={b.id} icon={<ShieldCheck size={17} />} title="Insurance & Verification"
          summary={insLabel} attention={insProblem || hasFatalAccident}
          open={open === "insurance"} onToggle={() => toggle("insurance")}>
          {(() => {
            const cur = b.coverage_selection || (b.insurance_type === "AllIn" ? "rental" : "own");
            const isAllIn = cur === "rental";
            const switchCoverage = async (sel, label) => {
              if (!window.confirm(`Switch this rental to ${label}?\n\nThe rental price will recalculate automatically for the whole rental (daily/weekly package, total, and balance owed) — Money and revenue reports update everywhere.`)) return;
              try {
                const { data } = await axios.post(`${API}/bookings/${b.id}/coverage`, { selection: sel }, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(`Now on ${data.label}. Price: $${data.old_total.toLocaleString()} → $${data.new_total.toLocaleString()} (${data.delta >= 0 ? "+" : ""}$${data.delta.toLocaleString()}). New balance: $${data.balance.toLocaleString()}.`);
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Could not switch coverage."); }
            };
            return (
              <div data-testid={`coverage-switch-${b.id}`} className="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                <span className="text-xs text-white/60">Insurance plan:</span>
                <span data-testid={`coverage-current-${b.id}`} className={`text-xs font-bold px-2 py-0.5 rounded-full border ${isAllIn ? "border-[#F2B705]/50 text-[#F2B705]" : "border-sky-500/40 text-sky-300"}`}>
                  {isAllIn ? "All-In Coverage" : cur === "decline" ? "Coverage Declined" : "Bring Your Own Insurance"}
                </span>
                {!isAllIn && (
                  <button data-testid={`coverage-to-allin-${b.id}`} onClick={() => switchCoverage("rental", "All-In Coverage")}
                    className="text-xs px-3 py-1.5 rounded border border-[#F2B705]/50 text-[#F2B705] hover:bg-[#F2B705] hover:text-black transition-colors">
                    Switch to All-In Coverage
                  </button>
                )}
                {isAllIn && (
                  <button data-testid={`coverage-to-byoi-${b.id}`} onClick={() => switchCoverage("own", "Bring Your Own Insurance")}
                    className="text-xs px-3 py-1.5 rounded border border-sky-500/40 text-sky-300 hover:bg-sky-500 hover:text-black transition-colors">
                    Switch to Bring Your Own Insurance
                  </button>
                )}
                <span className="text-[10px] text-white/35 w-full">Switching recalculates the rental price and balance automatically across all reports.</span>
              </div>
            );
          })()}
          {b.insurance_bring_later && (
            <span data-testid={`insurance-later-${b.id}`} className="inline-block text-xs text-amber-400 border border-amber-400/40 rounded px-2 py-0.5 bg-amber-400/[0.08] mb-2">⚠ Insurance at pickup</span>
          )}
          {b.insurance_card_path && <InsuranceVerify booking={b} token={token} onSaved={load} />}
          <ReturnCompliancePanel booking={b} token={token} onSaved={load} />
          {b.address && <p className="text-white/45 mt-3 text-sm">🏠 {b.address}</p>}
          {b.address && <AddressVerify booking={b} token={token} onSaved={load} />}
          {b.coverage_selection !== "decline" && (
            <div className="mt-3">
              <button data-testid={`require-waiver-${b.id}`}
                onClick={async () => {
                  try {
                    await axios.post(`${API}/bookings/${b.id}/require-damage-waiver`, { required: !b.add_damage_protection }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success(!b.add_damage_protection ? "Damage Waiver required ($15/day added)." : "Damage Waiver requirement removed.");
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Could not update waiver."); }
                }}
                className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors border ${b.add_damage_protection ? "border-white/20 text-white/60 hover:border-white/40" : "border-[#F2B705]/50 text-[#F2B705] hover:bg-[#F2B705] hover:text-black"}`}>
                {b.add_damage_protection ? "Remove Required Waiver" : "Require Damage Waiver ($15/day)"}
              </button>
            </div>
          )}
          <div className="mt-4">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <div className="text-xs uppercase tracking-widest text-white/40">Verification documents</div>
              <ReverifyDocs booking={b} token={token} onSent={load} />
              {b.reverify_docs && (
                <span data-testid={`reverify-pending-${b.id}`} className="text-[11px] flex items-center gap-1 text-amber-400 border border-amber-400/40 rounded px-2 py-0.5 bg-amber-400/[0.08]">
                  ⚠ Re-verification requested — waiting on customer
                </span>
              )}
            </div>
            {(b.license_front_path || b.license_back_path || b.selfie_path || b.insurance_card_path || b.extra_driver_license_path) ? (
              <div className="flex flex-wrap gap-3">
                <DocImage path={b.license_front_path} label="License Front" token={token} />
                <DocImage path={b.license_back_path} label="License Back" token={token} />
                <DocImage path={b.selfie_path} label="Selfie" token={token} />
                <DocImage path={b.insurance_card_path} label="Insurance" token={token} />
                <DocImage path={b.extra_driver_license_path} label="Extra Driver" token={token} />
              </div>
            ) : <p className="text-white/35 text-xs">No verification documents on file.</p>}
          </div>
        </Section>

        <Section id="rental" bookingId={b.id} icon={<Car size={17} />} title="Rental Management"
          summary={overdue ? `⚠️ OVERDUE — return was ${ret}` : weeklyOverdue ? `⚠️ Ongoing Weekly — past paid-through ${weeklyPaidThrough}` : ongoingWeekly ? `Ongoing Weekly Rental${weeklyNeedsAnchor ? " — no billing anchor set" : ` — paid through ${weeklyPaidThrough}`}` : `${statusLabel} | Return ${b.return_date}${b.return_time ? ` at ${to12h(b.return_time)}` : ""}`}
          attention={overdue || rollingDue || weeklyOverdue || weeklyNeedsAnchor}
          open={open === "rental"} onToggle={() => toggle("rental")}>
          <div className="flex flex-wrap items-center gap-2">
            <ExtendRental booking={b} token={token} onSaved={load} />
            <DailyRollingToggle booking={b} token={token} onSaved={load} />
            <WeeklyOngoingToggle booking={b} token={token} onSaved={load} />
            <AutoRenewToggle booking={b} token={token} onSaved={load} />
            <EditBookingDates booking={b} token={token} onSaved={load} />
            <RentalTimeline booking={b} token={token} />
            <SwapCar booking={b} token={token} onSaved={load} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span data-testid={`extensions-used-${b.id}`}
              className={`text-xs px-2.5 py-1 rounded-full border ${(b.ext_seq || 0) >= 3 ? "border-red-500/50 text-red-300 font-bold" : "border-white/15 text-white/60"}`}>
              Extensions used: {Math.min(b.ext_seq || 0, 3)} / 3{(b.ext_seq || 0) >= 3 ? " — LIMIT REACHED (lifetime cap, no reset until rental closes)" : ""}
            </span>
            <button data-testid={`send-self-service-${b.id}`} onClick={() => sendSelfServiceLink(b.id)}
              className="flex items-center gap-2 text-xs bg-[#F2B705]/15 border border-[#F2B705]/40 text-[#F2B705] px-3 py-1.5 rounded-md hover:bg-[#F2B705] hover:text-black transition-colors">
              <Send size={13} /> Send self-service link
            </button>
            <button data-testid={`toggle-escalation-${b.id}`} onClick={() => toggleEscalation(b)}
              className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border transition-colors ${b.escalation_stopped ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10 hover:brightness-125" : "text-red-300 border-red-500/40 bg-red-500/10 hover:brightness-125"}`}>
              <BellOff size={13} /> {b.escalation_stopped ? "Resume overdue auto-texts" : "Stop overdue auto-texts"}
            </button>
            <button data-testid={`cancel-booking-${b.id}`} onClick={() => cancelBooking(b.id)}
              className="text-xs flex items-center gap-1.5 border border-red-500/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors">
              <Ban size={13} /> Cancel Booking
            </button>
          </div>
          {b.escalation_stopped && <p className="text-white/40 text-xs mt-2">Overdue escalation is OFF for this rental.</p>}
        </Section>

        <Section id="inspections" bookingId={b.id} icon={<Camera size={17} />} title="Inspections & Damage"
          summary={hasDamage ? "⚠️ Damage charge on file" : "No open damage report"} attention={hasDamage}
          open={open === "inspections"} onToggle={() => toggle("inspections")}>
          <InspectionPanel bookingId={b.id} token={token} />
          <div className="mt-3">
            <DamageCharge booking={b} token={token} onSaved={load} />
          </div>
        </Section>

        <Section id="agreement" bookingId={b.id} icon={<FileText size={17} />} title="Accidents & Documents"
          summary={agrStatus} attention={!!b.agreement_stale}
          open={open === "agreement"} onToggle={() => toggle("agreement")}>
          <div className="mb-3">
            <EmailDetective booking={b} token={token} />
          </div>
          {agreement ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs px-2.5 py-1 rounded-full ${agreement.status === "signed" ? "bg-green-500/15 text-green-400" : "bg-yellow-500/15 text-yellow-400"}`}>
                {agreement.agreement_number} — {agreement.status === "signed" ? `Signed${agreement.signed_at ? ` ${fmt(agreement.signed_at)}` : ""}` : "Pending signature"}
              </span>
              <button data-testid={`agreement-pdf-${b.id}`} onClick={() => openPdf(agreement.id)}
                className="flex items-center gap-1.5 bg-[#F2B705] text-black text-xs font-bold px-3 py-1.5 rounded-md hover:bg-white transition-colors">
                <FileText size={13} /> View Agreement (PDF)
              </button>
              {agreement.status !== "signed" && (
                <>
                  <button data-testid={`agreement-resend-${b.id}`} onClick={() => resendLink(agreement.id)}
                    className="flex items-center gap-1.5 border border-[#F2B705]/30 text-[#F2B705] text-xs px-3 py-1.5 rounded-md hover:bg-[#F2B705] hover:text-black transition-colors">
                    <Send size={13} /> Resend signing link
                  </button>
                  <button data-testid={`agreement-mark-signed-${b.id}`} onClick={() => markSigned(agreement.id)}
                    className="flex items-center gap-1.5 border border-green-500/40 text-green-400 text-xs px-3 py-1.5 rounded-md hover:bg-green-500 hover:text-black transition-colors">
                    Mark as Signed (hard copy)
                  </button>
                </>
              )}
            </div>
          ) : (
            <p className="text-white/40 text-sm">{(b.status === "approved" || b.agreement_id) ? "Agreement created — manage it in the Agreements tab." : "No agreement yet — use Approve & Generate Agreement above."}</p>
          )}
          {b.agreement_stale && (
            <div className="flex items-center gap-2 flex-wrap mt-3">
              <span data-testid={`agreement-stale-${b.id}`} className="text-xs flex items-center gap-1.5 text-amber-400 border border-amber-400/40 rounded px-2 py-1 bg-amber-400/[0.08]">
                ⚠ {b.agreement_stale_reason || "Agreement out of date — re-issue agreement"}
              </span>
              <button data-testid={`reissue-agreement-${b.id}`} onClick={() => reissueAgreement(b.id)}
                className="text-xs flex items-center gap-1.5 bg-[#F2B705] text-black font-bold px-3 py-1.5 rounded hover:bg-white transition-colors">
                <FileSignature size={13} /> Re-issue Agreement
              </button>
            </div>
          )}
          {(b.extra_docs || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {(b.extra_docs || []).map((d, i) => <DocFile key={i} path={d.path} label={d.label} token={token} sub={`by ${d.uploaded_by || "admin"}`} />)}
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="text-[11px] uppercase tracking-widest text-white/35 mb-1.5">Upload a document for this renter</div>
            <AdminDocUpload entityType="booking" entityId={b.id} token={token} onUploaded={load} />
          </div>
        </Section>

        <Section id="club" bookingId={b.id} icon={<Gift size={17} />} title="Club & Customer Tools"
          summary="Loyalty points & renter risk" attention={false}
          open={open === "club"} onToggle={() => toggle("club")}>
          <div className="flex flex-wrap items-center gap-2">
            <ClubPointsButton booking={b} token={token} onSaved={load} />
            <RiskBadge phone={b.phone} token={token} />
          </div>
        </Section>
      </div>
    </div>
  );
}
